import { api } from '@/lib/axios'
import type {
  AttachmentLinkSettings,
  MailComposeDraft,
  MailDispatchRow,
  MailSignatureView,
  MailSourceType,
  MailConnectionStatus,
  MailConnectionView,
  MailQuotaStatus,
  Microsoft365Policy,
  Microsoft365Settings,
} from '@symphenia/shared-types'

/**
 * AP-10 Nachtrag 2 — Einstellungen des Mailservices. Geteilt unter `lib/`, weil
 * Mandanten-Einstellungen, Belegwesen und das Profil es brauchen.
 *
 * Nachtrag 3: Stand des Mail-Kontingents (nur lesend) und die Anfrage nach mehr Mails.
 * Nachtrag 1: Microsoft-365-Richtlinie, die eigene Postfachverbindung und die
 * Verbindungen des Mandanten.
 * Nachtrag 5: Entwurf für den Exportdialog, eigene Signatur, Versandprotokoll.
 * Gesendet wird über `exportMailen` in `@/lib/export-request`.
 */
export interface Microsoft365Integration {
  /** Hat der Betrieb die Microsoft-App eingerichtet? Sonst ist alles ausgeblendet. */
  available: boolean
  adminConsentUrl: string | null
}

export interface MailSettings {
  attachmentLinks: AttachmentLinkSettings
  quota?: MailQuotaStatus
  microsoft365?: Microsoft365Settings
  microsoft365Integration?: Microsoft365Integration
}

export interface MailQuotaRequestResult {
  status: 'SENT' | 'ALREADY_SENT'
  requestedAt: string
}

export interface MailConnectionState {
  available: boolean
  policy: Microsoft365Policy
  mayConnect: boolean
  connection: MailConnectionView | null
}

export interface TenantMailConnection {
  userId: string
  userName: string
  address: string
  status: MailConnectionStatus
  connectedAt: string
  lastUsedAt: string | null
}

export const mailApi = {
  getSettings: () => api.get<{ data: Required<MailSettings> }>('/mail/settings'),
  updateSettings: (payload: { attachmentLinks: AttachmentLinkSettings }) =>
    api.put<{ data: Required<MailSettings> }>('/mail/settings', payload),
  /** Geht über die Modulanfragen (AP-29-F) — dieselbe Sperrfrist, dasselbe Protokoll. */
  requestMoreMails: async (additionalMails: number, note?: string): Promise<MailQuotaRequestResult> => {
    const res = await api.post<MailQuotaRequestResult>('/module-requests', {
      moduleKey: 'CORE',
      kind: 'MAIL_QUOTA',
      additionalMails,
      ...(note?.trim() ? { note: note.trim() } : {}),
    })
    return res.data
  },

  // ── Nachtrag 1: Microsoft 365 ──────────────────────────────────────────────
  /** Umstellen auf „aus" löscht alle Verbindungen des Mandanten. */
  updateMicrosoft365: (payload: Microsoft365Settings) =>
    api.put<{ data: { microsoft365: Microsoft365Settings; removedConnections: number; microsoft365Integration: Microsoft365Integration } }>(
      '/mail/settings/microsoft365',
      payload,
    ),
  getConnection: () => api.get<{ data: MailConnectionState }>('/mail/connection'),
  /** Liefert die Anmeldeseite von Microsoft — der Browser wird dorthin geschickt. */
  startMicrosoftConnection: () => api.post<{ data: { authorizeUrl: string } }>('/mail/connection/microsoft'),
  disconnect: () => api.delete<{ message: string }>('/mail/connection'),
  listConnections: (page = 1, limit = 25) =>
    api.get<{ data: TenantMailConnection[]; meta: { total: number; page: number; limit: number; totalPages: number } }>('/mail/connections', {
      params: { page, limit },
    }),
  disconnectUser: (userId: string) => api.delete<{ message: string }>(`/mail/connections/${userId}`),

  // ── Nachtrag 5: Exporte per E-Mail ────────────────────────────────────────
  compose: (source: MailSourceType) => api.get<{ data: MailComposeDraft }>('/mail/compose', { params: { source } }),
  getSignature: () => api.get<{ data: MailSignatureView }>('/mail/signature'),
  /** Leer setzt auf die Vorgabe zurück. */
  updateSignature: (signature: string | null) => api.put<{ data: MailSignatureView }>('/mail/signature', { signature }),
  listDispatches: (params: Record<string, unknown>) =>
    api.get<{ data: MailDispatchRow[]; meta: { total: number; page: number; limit: number; totalPages: number } }>('/mail/dispatches', { params }),
}
